import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry && !orig.url.includes("/auth/")) {
      orig._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(orig);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function errText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=out, obj=in

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(false);
  };
  return <AuthContext.Provider value={{ user, setUser, login, register, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

// ---- offline helpers ----
export const cacheSet = (key, data) => {
  try { localStorage.setItem(`fscd_${key}`, JSON.stringify(data)); } catch (e) {}
};
export const cacheGet = (key) => {
  try { const v = localStorage.getItem(`fscd_${key}`); return v ? JSON.parse(v) : null; } catch (e) { return null; }
};

const QUEUE_KEY = "fscd_pending_completions";
export const queueCompletion = (trackId, lessonId) => {
  const q = cacheGet("pending_completions") || [];
  if (!q.find((x) => x.trackId === trackId && x.lessonId === lessonId)) {
    q.push({ trackId, lessonId });
    localStorage.setItem(`fscd_pending_completions`, JSON.stringify(q));
  }
};
export const flushQueue = async () => {
  const q = cacheGet("pending_completions") || [];
  if (!q.length) return 0;
  const remaining = [];
  for (const item of q) {
    try { await api.post(`/tracks/${item.trackId}/lessons/${item.lessonId}/complete`); }
    catch (e) { remaining.push(item); }
  }
  localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
  return q.length - remaining.length;
};

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => { setOnline(true); flushQueue(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}
